import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";

import {
  FiUsers,
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiPhone,
  FiMail,
  FiMessageCircle,
  FiUserPlus,
  FiClock,
  FiAlertCircle,
  FiCheckCircle
} from "react-icons/fi";

import { useNavigate } from "react-router-dom";

import api from "../../utills/api";


/* =========================================================
   HELPERS
========================================================= */

const getLeadId = (lead) => {
  return lead?._id || lead?.id;
};


const getLeadName = (lead) => {
  return (
    lead?.name ||
    lead?.studentName ||
    [
      lead?.firstName,
      lead?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    "Unnamed Lead"
  );
};


const getPhone = (lead) => {
  return (
    lead?.phone ||
    lead?.mobile ||
    lead?.whatsapp ||
    lead?.alternatePhone ||
    ""
  );
};


const getEmail = (lead) => {
  return lead?.email || "";
};


const getSource = (lead) => {
  return (
    lead?.source ||
    lead?.leadSource ||
    "Unknown"
  );
};


const getPriority = (lead) => {
  return (
    lead?.priority ||
    "Medium"
  );
};


const getStatus = (lead) => {
  return (
    lead?.status ||
    lead?.leadStatus ||
    "New"
  );
};


const getCourse = (lead) => {
  return (
    lead?.course ||
    lead?.program ||
    lead?.courseInterest ||
    lead?.programInterest ||
    lead?.courseName ||
    "-"
  );
};


const getAssignedTo = (lead) => {
  if (
    typeof lead?.assignedTo ===
    "object"
  ) {
    return (
      lead.assignedTo?.name ||
      lead.assignedTo?.email ||
      "Unassigned"
    );
  }

  return (
    lead?.assignedTo ||
    lead?.counsellor ||
    "Unassigned"
  );
};


const getCreatedDate = (lead) => {
  return (
    lead?.createdAt ||
    lead?.createdDate ||
    null
  );
};


const formatDate = (date) => {
  if (!date) {
    return "-";
  }

  const parsed =
    new Date(date);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return "-";
  }

  return parsed.toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};


const getInitials = (name) => {
  return String(name || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(
      (part) =>
        part.charAt(0).toUpperCase()
    )
    .join("");
};


/* =========================================================
   COMPONENT
========================================================= */

const NewLeads = () => {
  const navigate = useNavigate();

  const [leads, setLeads] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [priorityFilter, setPriorityFilter] =
    useState("All");

  const [sourceFilter, setSourceFilter] =
    useState("All");


  /* =======================================================
     FETCH LEADS
  ======================================================= */

  const fetchLeads = async (
    isRefresh = false
  ) => {
    try {
      setError("");

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response =
        await api.get("/leads");

      const data =
        response?.data?.data;

      let leadList = [];

      if (Array.isArray(data)) {
        leadList = data;
      } else if (
        Array.isArray(data?.leads)
      ) {
        leadList = data.leads;
      }

      /*
       * New Leads page only displays
       * leads currently having status New.
       */

      const newLeadList =
        leadList.filter(
          (lead) =>
            String(
              getStatus(lead)
            ).toLowerCase() ===
            "new"
        );

      setLeads(newLeadList);
    } catch (err) {
      console.error(
        "New Leads fetch error:",
        err
      );

      setError(
        err?.response?.data?.message ||
        "Unable to load new leads."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  useEffect(() => {
    fetchLeads();
  }, []);


  /* =======================================================
     FILTER OPTIONS
  ======================================================= */

  const sourceOptions =
    useMemo(() => {
      const values =
        leads
          .map(
            (lead) =>
              getSource(lead)
          )
          .filter(Boolean);

      return [
        ...new Set(values),
      ].sort();
    }, [leads]);


  /* =======================================================
     FILTERED LEADS
  ======================================================= */

  const filteredLeads =
    useMemo(() => {
      const searchValue =
        search
          .trim()
          .toLowerCase();

      return leads.filter(
        (lead) => {
          const name =
            getLeadName(
              lead
            ).toLowerCase();

          const phone =
            getPhone(
              lead
            ).toLowerCase();

          const email =
            getEmail(
              lead
            ).toLowerCase();

          const course =
            getCourse(
              lead
            ).toLowerCase();

          const source =
            getSource(
              lead
            ).toLowerCase();

          const matchesSearch =
            !searchValue ||
            name.includes(
              searchValue
            ) ||
            phone.includes(
              searchValue
            ) ||
            email.includes(
              searchValue
            ) ||
            course.includes(
              searchValue
            ) ||
            source.includes(
              searchValue
            );

          const matchesPriority =
            priorityFilter ===
              "All" ||
            getPriority(
              lead
            ) ===
              priorityFilter;

          const matchesSource =
            sourceFilter ===
              "All" ||
            getSource(
              lead
            ) ===
              sourceFilter;

          return (
            matchesSearch &&
            matchesPriority &&
            matchesSource
          );
        }
      );
    }, [
      leads,
      search,
      priorityFilter,
      sourceFilter,
    ]);


  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary =
    useMemo(() => {
      const high =
        leads.filter(
          (lead) =>
            getPriority(
              lead
            ) === "High"
        ).length;

      const medium =
        leads.filter(
          (lead) =>
            getPriority(
              lead
            ) === "Medium"
        ).length;

      const low =
        leads.filter(
          (lead) =>
            getPriority(
              lead
            ) === "Low"
        ).length;

      return {
        total: leads.length,
        high,
        medium,
        low,
      };
    }, [leads]);


  /* =======================================================
     CLEAR FILTERS
  ======================================================= */

  const clearFilters = () => {
    setSearch("");
    setPriorityFilter("All");
    setSourceFilter("All");
  };


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>

          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "400px",
            }}
          >
            <Spinner size="sm" />
          </div>

        </Container>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="page-content">
      <Container fluid>

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="d-flex justify-content-between align-items-center mb-4">

          <div>
            <h4 className="mb-1">
              New Leads
            </h4>

            <p className="text-muted mb-0">
              Manage newly received
              education leads.
            </p>
          </div>


          <div className="d-flex gap-2">

            <Button
              color="light"
              onClick={() =>
                fetchLeads(true)
              }
              disabled={refreshing}
            >
              {refreshing ? (
                <Spinner
                  size="sm"
                  className="me-2"
                />
              ) : (
                <FiRefreshCw
                  size={15}
                  className="me-2"
                />
              )}

              Refresh
            </Button>


            <Button
              color="primary"
              onClick={() =>
                navigate("/leads")
              }
            >
              <FiUsers
                size={15}
                className="me-2"
              />

              All Leads
            </Button>

          </div>

        </div>


        {/* =================================================
            ERROR
        ================================================= */}

        {error && (
          <Alert color="danger">
            <FiAlertCircle
              className="me-2"
            />

            {error}
          </Alert>
        )}


        {/* =================================================
            SUMMARY CARDS
        ================================================= */}

        <Row>

          <Col xl={3} md={6}>
            <Card>
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      New Leads
                    </p>

                    <h4 className="mb-0">
                      {summary.total}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-20">
                      <FiUserPlus />
                    </span>
                  </div>

                </div>

              </CardBody>
            </Card>
          </Col>


          <Col xl={3} md={6}>
            <Card>
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      High Priority
                    </p>

                    <h4 className="mb-0">
                      {summary.high}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title rounded-circle bg-danger-subtle text-danger fs-20">
                      <FiAlertCircle />
                    </span>
                  </div>

                </div>

              </CardBody>
            </Card>
          </Col>


          <Col xl={3} md={6}>
            <Card>
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      Medium Priority
                    </p>

                    <h4 className="mb-0">
                      {summary.medium}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title rounded-circle bg-warning-subtle text-warning fs-20">
                      <FiClock />
                    </span>
                  </div>

                </div>

              </CardBody>
            </Card>
          </Col>


          <Col xl={3} md={6}>
            <Card>
              <CardBody>

                <div className="d-flex justify-content-between">

                  <div>
                    <p className="text-muted mb-2">
                      Low Priority
                    </p>

                    <h4 className="mb-0">
                      {summary.low}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title rounded-circle bg-success-subtle text-success fs-20">
                      <FiCheckCircle />
                    </span>
                  </div>

                </div>

              </CardBody>
            </Card>
          </Col>

        </Row>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Card>
          <CardBody>

            <Row className="g-3 align-items-end">

              <Col
                lg={5}
                md={12}
              >

                <label className="form-label">
                  Search
                </label>

                <div className="position-relative">

                  <FiSearch
                    className="position-absolute text-muted"
                    style={{
                      left: "12px",
                      top: "11px",
                    }}
                    size={16}
                  />

                  <Input
                    type="text"
                    placeholder="Search by name, phone, email or course..."
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                    style={{
                      paddingLeft:
                        "36px",
                    }}
                  />

                </div>

              </Col>


              <Col
                lg={3}
                md={6}
              >

                <label className="form-label">
                  Priority
                </label>

                <Input
                  type="select"
                  value={
                    priorityFilter
                  }
                  onChange={(e) =>
                    setPriorityFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="All">
                    All Priorities
                  </option>

                  <option value="High">
                    High
                  </option>

                  <option value="Medium">
                    Medium
                  </option>

                  <option value="Low">
                    Low
                  </option>
                </Input>

              </Col>


              <Col
                lg={3}
                md={6}
              >

                <label className="form-label">
                  Source
                </label>

                <Input
                  type="select"
                  value={
                    sourceFilter
                  }
                  onChange={(e) =>
                    setSourceFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="All">
                    All Sources
                  </option>

                  {sourceOptions.map(
                    (source) => (
                      <option
                        key={source}
                        value={source}
                      >
                        {source}
                      </option>
                    )
                  )}

                </Input>

              </Col>


              <Col lg={1} md={12}>

                <Button
                  color="light"
                  className="w-100"
                  onClick={
                    clearFilters
                  }
                >
                  Clear
                </Button>

              </Col>

            </Row>

          </CardBody>
        </Card>


        {/* =================================================
            LEADS TABLE
        ================================================= */}

        <Card>

          <CardBody>

            <div className="d-flex justify-content-between align-items-center mb-3">

              <div>

                <h5 className="card-title mb-1">
                  New Leads
                </h5>

                <p className="text-muted mb-0">
                  Showing{" "}
                  <strong>
                    {filteredLeads.length}
                  </strong>{" "}
                  of{" "}
                  <strong>
                    {leads.length}
                  </strong>{" "}
                  new leads
                </p>

              </div>

            </div>


            {filteredLeads.length ===
            0 ? (

              <div className="text-center py-5">

                <div className="avatar-md mx-auto mb-3">
                  <span className="avatar-title rounded-circle bg-light text-muted fs-24">
                    <FiUsers />
                  </span>
                </div>

                <h5>
                  No new leads found
                </h5>

                <p className="text-muted mb-3">
                  No leads match the
                  current search and
                  filters.
                </p>

                <Button
                  color="light"
                  onClick={
                    clearFilters
                  }
                >
                  Clear Filters
                </Button>

              </div>

            ) : (

              <div className="table-responsive">

                <table className="table table-hover align-middle mb-0">

                  <thead className="table-light">

                    <tr>

                      <th>
                        Lead
                      </th>

                      <th>
                        Contact
                      </th>

                      <th>
                        Course / Program
                      </th>

                      <th>
                        Source
                      </th>

                      <th>
                        Priority
                      </th>

                      <th>
                        Assigned To
                      </th>

                      <th>
                        Created
                      </th>

                      <th>
                        Action
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {filteredLeads.map(
                      (lead) => {

                        const id =
                          getLeadId(
                            lead
                          );

                        const name =
                          getLeadName(
                            lead
                          );

                        const phone =
                          getPhone(
                            lead
                          );

                        const email =
                          getEmail(
                            lead
                          );

                        const priority =
                          getPriority(
                            lead
                          );

                        return (
                          <tr
                            key={id}
                          >

                            {/* LEAD */}

                            <td>

                              <div className="d-flex align-items-center">

                                <div
                                  className="avatar-xs me-2"
                                >
                                  <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                    {getInitials(
                                      name
                                    )}
                                  </span>
                                </div>

                                <div>

                                  <div className="fw-medium">
                                    {name}
                                  </div>

                                  <div className="text-muted small">
                                    {id
                                      ? `ID: ${String(
                                          id
                                        ).slice(
                                          -8
                                        )}`
                                      : "-"}
                                  </div>

                                </div>

                              </div>

                            </td>


                            {/* CONTACT */}

                            <td>

                              {phone && (
                                <div className="small">
                                  <FiPhone
                                    size={12}
                                    className="me-1"
                                  />

                                  {phone}
                                </div>
                              )}

                              {email && (
                                <div className="small text-muted">
                                  <FiMail
                                    size={12}
                                    className="me-1"
                                  />

                                  {email}
                                </div>
                              )}

                              {!phone &&
                                !email && (
                                  <span className="text-muted">
                                    -
                                  </span>
                                )}

                            </td>


                            {/* COURSE */}

                            <td>
                              {getCourse(
                                lead
                              )}
                            </td>


                            {/* SOURCE */}

                            <td>
                              <Badge
                                color="light"
                                className="text-dark"
                              >
                                {getSource(
                                  lead
                                )}
                              </Badge>
                            </td>


                            {/* PRIORITY */}

                            <td>

                              <Badge
                                color={
                                  priority ===
                                  "High"
                                    ? "danger"
                                    : priority ===
                                      "Low"
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {priority}
                              </Badge>

                            </td>


                            {/* ASSIGNED */}

                            <td>
                              {getAssignedTo(
                                lead
                              )}
                            </td>


                            {/* CREATED */}

                            <td>
                              {formatDate(
                                getCreatedDate(
                                  lead
                                )
                              )}
                            </td>


                            {/* ACTION */}

                            <td>

                              <div className="d-flex gap-1">

                                <Button
                                  color="light"
                                  size="sm"
                                  title="View lead"
                                  onClick={() =>
                                    navigate(
                                      `/leads/${id}`
                                    )
                                  }
                                >
                                  <FiEye
                                    size={14}
                                  />
                                </Button>


                                {phone && (
                                  <Button
                                    color="light"
                                    size="sm"
                                    title="Call"
                                    onClick={() =>
                                      window.open(
                                        `tel:${phone}`,
                                        "_self"
                                      )
                                    }
                                  >
                                    <FiPhone
                                      size={14}
                                    />
                                  </Button>
                                )}


                                {email && (
                                  <Button
                                    color="light"
                                    size="sm"
                                    title="Email"
                                    onClick={() =>
                                      window.open(
                                        `mailto:${email}`,
                                        "_self"
                                      )
                                    }
                                  >
                                    <FiMail
                                      size={14}
                                    />
                                  </Button>
                                )}


                                {phone && (
                                  <Button
                                    color="light"
                                    size="sm"
                                    title="WhatsApp"
                                    onClick={() =>
                                      window.open(
                                        `https://wa.me/${String(
                                          phone
                                        ).replace(
                                          /\D/g,
                                          ""
                                        )}`,
                                        "_blank",
                                        "noopener,noreferrer"
                                      )
                                    }
                                  >
                                    <FiMessageCircle
                                      size={14}
                                    />
                                  </Button>
                                )}

                              </div>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            )}

          </CardBody>

        </Card>

      </Container>
    </div>
  );
};

export default NewLeads;